import React from 'react';
import { Link } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Button,
    Grid,
    Card,
    CardContent,
    Paper,
    Stack,
    Avatar,
    Rating,
    IconButton,
    Chip,
    Divider,
    useTheme,
    alpha,
    Fade,
    Slide,
    Zoom
} from '@mui/material';
import ScheduleCallModal from '../components/ScheduleCallModal';
import {
    School,
    Groups,
    EmojiEvents,
    ArrowForward,
    Facebook,
    Twitter,
    Instagram,
    LinkedIn,
    YouTube,
    Star,
    TrendingUp,
    Psychology,
    Diversity3,
    AutoAwesome,
    CheckCircle,
    Phone,
    Email,
    LocationOn,
    Verified
} from '@mui/icons-material';

const Home = () => {
    const theme = useTheme();

    const stats = [
      { number: '100%', label: 'Success Rate', icon: <TrendingUp /> },
      { number: '3:1', label: 'Teacher Ratio', icon: <School /> },
      { number: '20+', label: 'Years Experience', icon: <EmojiEvents /> }
    ];

    const features = [
      {
        icon: <School sx={{ fontSize: 60 }} />,
        title: 'Academic Excellence',
        description: 'Our rigorous curriculum and dedicated teachers ensure every student reaches their full academic potential with personalized learning approaches.',
        color: theme.palette.primary.main,
        highlights: ['Personalized Learning', 'Expert Faculty', 'Modern Curriculum']
      },
      {
        icon: <Groups sx={{ fontSize: 60 }} />,
        title: 'Strong Community',
        description: 'Build lasting friendships and connections in our supportive environment where every student, family, and teacher is valued.',
        color: theme.palette.secondary.main,
        highlights: ['Supportive Environment', 'Family Values', 'Lasting Connections']
      },
      {
        icon: <EmojiEvents sx={{ fontSize: 60 }} />,
        title: 'Character Development',
        description: 'We nurture not just academic growth but also moral character, leadership skills, and values that last a lifetime.',
        color: theme.palette.success.main,
        highlights: ['Leadership Skills', 'Moral Values', 'Life Skills']
      }
    ];

    const reviews = [
      {
        name: 'Ashley W',
        role: 'Parent of 1st Grader',
        content: 'Eagles Refuge Academy has exceeded my expectations. The teachers genuinely care about each child\'s success, and the small class sizes allow for personalized attention that has helped my child thrive academically and socially.',
        rating: 5,
        avatar: 'A',
        verified: true
      },
    ];

    return (
      <Box>
        {/* Modern Hero Section */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.95)} 0%, ${alpha(theme.palette.primary.main, 0.9)} 100%), url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 1000"><polygon fill="${alpha('#ffffff', 0.1)}" points="0,1000 1000,0 1000,1000"/></svg>')`,
            color: '#f5f5f5',
            py: { xs: 6, md: 10 },
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden',
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Container maxWidth="lg" sx={{ width: '100%', px: { xs: 2, sm: 3 } }}>
            <Fade in timeout={1000}>
              <Box>
                {/* School Logo with Animation */}
                <Zoom in timeout={1200}>
                  <Box sx={{ mb: 0, flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Box
                      component="img"
                      src={process.env.PUBLIC_URL + "/main-logo.png"}
                      alt="Eagles Refuge Academy Logo"
                      sx={{
                        width: { xs: '90%', md: '95%' },
                        maxWidth: '1400px',
                        height: 'auto',
                        mx: 'auto',
                        display: 'block',
                        borderRadius: 6,
                        bgcolor: 'white',
                        p: { xs: 2, sm: 4, md: 8 },
                        boxShadow: `0 30px 60px ${alpha('#000000', 0.5)}`,
                        boxSizing: 'border-box'
                      }}
                    />
                  </Box>
                </Zoom>

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  justifyContent="center"
                  sx={{ mb: 4, mt: 2 }}
                >
                  <Button
                    component={Link}
                    to="/apply"
                    variant="contained"
                    size="large"
                    endIcon={<ArrowForward />}
                    sx={{
                      bgcolor: '#f5f5f5',
                      color: 'primary.main',
                      px: { xs: 3, sm: 6 },
                      py: 2.5,
                      fontSize: { xs: '1rem', sm: '1.2rem' },
                      fontWeight: 600,
                      borderRadius: 3,
                      boxShadow: `0 8px 32px ${alpha('#000000', 0.2)}`,
                      '&:hover': {
                        bgcolor: 'grey.100',
                        transform: 'translateY(-3px)',
                        boxShadow: `0 12px 40px ${alpha('#000000', 0.3)}`
                      },
                      transition: 'all 0.3s ease',
                      whiteSpace: 'nowrap',
                      textDecoration: 'none'
                    }}
                  >
                    Apply Today
                  </Button>
                </Stack>

                {/* Stats Section */}
                <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mt: { xs: 4, md: 0 } }}>
                  <Grid container spacing={3} sx={{ maxWidth: 800, display: 'flex', justifyContent: 'center' }}>
                    {stats.map((stat, index) => (
                      <Grid item xs={6} md={3} key={index}>
                        <Fade in timeout={1000 + (index * 300)}>
                          <Slide direction="up" in timeout={1200 + (index * 300)}>
                            <Paper
                              elevation={4}
                              sx={{
                                p: { xs: 2, md: 3 },
                                textAlign: 'center',
                                background: `linear-gradient(135deg, ${alpha('#f5f5f5', 0.2)} 0%, ${alpha('#f5f5f5', 0.1)} 100%)`,
                                backdropFilter: 'blur(10px)',
                                border: `1px solid ${alpha('#f5f5f5', 0.3)}`,
                                borderRadius: 4,
                                color: '#f5f5f5',
                                boxShadow: `0 10px 20px ${alpha('#000', 0.2)}`,
                                transition: 'all 0.3s ease',
                                overflow: 'hidden',
                                position: 'relative',
                                '&:hover': {
                                  transform: 'translateY(-5px)',
                                  boxShadow: `0 15px 30px ${alpha('#000', 0.3)}`,
                                  background: `linear-gradient(135deg, ${alpha('#f5f5f5', 0.25)} 0%, ${alpha('#f5f5f5', 0.15)} 100%)`
                                },
                                '&::after': {
                                  content: '""',
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  width: '100%',
                                  height: '100%',
                                  background: `linear-gradient(135deg, ${alpha('#fff', 0.1)} 0%, transparent 50%)`,
                                  pointerEvents: 'none'
                                }
                              }}
                            >
                              <Box sx={{ 
                                color: '#f5f5f5', 
                                mb: 1.5,
                                fontSize: { xs: '1.5rem', md: '2rem' },
                                '& > svg': {
                                  fontSize: { xs: '2rem', md: '2.5rem' },
                                  filter: 'drop-shadow(0 2px 5px rgba(0,0,0,0.2))'
                                }
                              }}>
                                {stat.icon}
                              </Box>
                              <Typography 
                                variant="h4" 
                                sx={{ 
                                  fontWeight: 800, 
                                  mb: 1, 
                                  textShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                  fontSize: { xs: '1.5rem', md: '2.25rem' }
                                }}
                              >
                                {stat.number}
                              </Typography>
                              <Typography 
                                variant="body1" 
                                sx={{ 
                                  opacity: 0.9, 
                                  fontWeight: 500,
                                  fontSize: { xs: '0.875rem', md: '1rem' }
                                }}
                              >
                                {stat.label}
                              </Typography>
                            </Paper>
                          </Slide>
                        </Fade>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </Box>
            </Fade>
          </Container>
        </Box>

        {/* Enhanced Feature Cards */}
        <Box sx={{ py: 15, bgcolor: 'grey.50' }}>
          <Container maxWidth="lg" sx={{ width: '100%' }}>
            <Fade in timeout={800}>
              <Box sx={{ textAlign: 'center', mb: 10 }}>
                <Chip
                  label="Why Choose Us"
                  sx={{
                    mb: 3,
                    px: 3,
                    py: 1,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: 'primary.main',
                    fontWeight: 600,
                    fontSize: '1rem'
                  }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
                  <Typography
                    variant="h2"
                    sx={{
                      fontWeight: 800,
                      fontSize: { xs: '2.5rem', md: '3.5rem' },
                      background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent'
                    }}
                  >
                    Why Choose Eagles Refuge Academy?
                  </Typography>
                </Box>
                <Typography
                  variant="h6"
                  color="text.secondary"
                  sx={{ maxWidth: 700, mx: 'auto', fontSize: '1.3rem', lineHeight: 1.6 }}
                >
                  Discover what makes our school the perfect place for your child's growth and success
                </Typography>
              </Box>
            </Fade>

            <Grid container spacing={3} sx={{ width: '100%' }}>
              {features.map((feature, index) => (
                <Grid item xs={12} md={4} key={index} sx={{ width: '100%' }}>
                  <Fade in timeout={1000 + index * 200}>
                    <Card
                      elevation={0}
                      sx={{
                        height: '100%',
                        width: '100%',
                        p: { xs: 2, md: 4 },
                        textAlign: 'center',
                        bgcolor: '#FFFFFF',
                        borderRadius: 4,
                        border: `1px solid ${alpha(feature.color, 0.1)}`,
                        position: 'relative',
                        overflow: 'hidden',
                        transition: 'all 0.4s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-15px)',
                          boxShadow: `0 25px 50px ${alpha(feature.color, 0.15)}`,
                          '&::before': {
                            transform: 'translateY(0)'
                          }
                        },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: 4,
                          background: `linear-gradient(90deg, ${feature.color}, ${alpha(feature.color, 0.7)})`,
                          transform: 'translateY(-4px)',
                          transition: 'transform 0.3s ease'
                        }
                      }}
                    >
                      <CardContent sx={{ p: 0, width: '100%' }}>
                        <Avatar
                          sx={{
                            bgcolor: alpha(feature.color, 0.1),
                            color: feature.color,
                            width: 80,
                            height: 80,
                            mx: 'auto',
                            mb: 3
                          }}
                        >
                          {feature.icon}
                        </Avatar>

                        <Typography
                          variant="h4"
                          sx={{
                            fontWeight: 700,
                            mb: 3,
                            color: feature.color,
                            width: '100%'
                          }}
                        >
                          {feature.title}
                        </Typography>

                        <Typography
                          variant="body1"
                          color="text.secondary"
                          sx={{
                            lineHeight: 1.8,
                            mb: 4,
                            fontSize: '1.1rem',
                            width: '100%'
                          }}
                        >
                          {feature.description}
                        </Typography>

                        <Stack spacing={1} sx={{ width: '100%' }}>
                          {feature.highlights.map((highlight, idx) => (
                            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <CheckCircle sx={{ color: feature.color, mr: 1, fontSize: 18 }} />
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {highlight}
                              </Typography>
                            </Box>
                          ))}
                        </Stack>
                      </CardContent>
                    </Card>
                  </Fade>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Enhanced Reviews Section */}
        <Box sx={{ py: 15, bgcolor: '#FFFFFF' }}>
          <Container maxWidth="lg">
            <Fade in timeout={800}>
              <Box sx={{ textAlign: 'center', mb: 10 }}>
                <Chip
                  label="Testimonials"
                  sx={{
                    mb: 3,
                    px: 3,
                    py: 1,
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    color: 'success.main',
                    fontWeight: 600,
                    fontSize: '1rem'
                  }}
                />
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 800,
                    mb: 3,
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                  }}
                >
                    What Our Families Say
                </Typography>
                  <Typography
                      variant="h6"
                      color="text.secondary"
                      sx={{ maxWidth: 700, mx: 'auto', fontSize: '1.3rem', lineHeight: 1.6 }}
                  >
                      Hear from parents and students who are part of our community
                  </Typography>
              </Box>
            </Fade>

              <Grid container spacing={5}>
                  {reviews.map((review, index) => (
                      <Grid item xs={12} md={4} key={index}>
                          <Fade in timeout={1000 + index * 200}>
                              <Card
                                  elevation={0}
                                  sx={{
                                      height: '100%',
                                      p: 4,
                                      bgcolor: '#FFFFFF',
                                      borderRadius: 4,
                                      border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                                      position: 'relative',
                                      overflow: 'hidden',
                                      transition: 'all 0.4s ease-in-out',
                                      '&:hover': {
                                          transform: 'translateY(-10px)',
                                          boxShadow: `0 20px 40px ${alpha(theme.palette.grey[500], 0.15)}`,
                                      }
                                  }}
                              >
                                  <CardContent sx={{ p: 0 }}>
                                      {/* Rating */}
                                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                          <Rating
                                              value={review.rating}
                                              readOnly
                                              sx={{ color: '#ffc107', mr: 2 }}
                                              size="large"
                                          />
                                          {review.verified && (
                                              <Chip
                                                  icon={<Verified sx={{ fontSize: 16 }} />}
                                                  label="Verified"
                                                  size="small"
                                                  sx={{
                                                      bgcolor: alpha(theme.palette.success.main, 0.1),
                                                      color: 'success.main',
                                                      fontWeight: 600,
                                                      fontSize: '0.75rem'
                                                  }}
                                              />
                                          )}
                                      </Box>

                                      {/* Review Content */}
                                      <Typography
                                          variant="body1"
                                          sx={{
                                              mb: 4,
                                              fontStyle: 'italic',
                                              lineHeight: 1.8,
                                              fontSize: '1.1rem',
                                              color: 'text.secondary',
                                              position: 'relative',
                                              '&::before': {
                                                  content: 'none',
                                                  position: 'absolute',
                                                  top: -10,
                                                  left: -10,
                                                  fontSize: '3rem',
                                                  color: alpha(theme.palette.primary.main, 0.2),
                                                  fontFamily: 'serif'
                                              }
                                          }}
                                      >
                                          {review.content}
                                      </Typography>

                                      {/* Reviewer Info */}
                                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                          <Avatar
                                              sx={{
                                                  mr: 3,
                                                  bgcolor: theme.palette.primary.main,
                                                  width: 56,
                                                  height: 56,
                                                  fontSize: '1.5rem',
                                                  fontWeight: 600
                                              }}
                                          >
                                              {review.avatar}
                                          </Avatar>
                                          <Box>
                                              <Typography
                                                  variant="h6"
                                                  sx={{
                                                      fontWeight: 700,
                                                      color: 'text.primary',
                                                      mb: 0.5
                                                  }}
                                              >
                                                  {review.name}
                                              </Typography>
                                              <Typography
                                                  variant="body2"
                                                  sx={{
                                                      color: 'text.secondary',
                                                      fontWeight: 500
                                                  }}
                                              >
                                                  {review.role}
                                              </Typography>
                                          </Box>
                                      </Box>
                                  </CardContent>
                              </Card>
                          </Fade>
                      </Grid>
                  ))}
              </Grid>
          </Container>
        </Box>

          {/* Call to Action Section */}
          <Box
              sx={{
                  py: 15,
                  bgcolor: 'grey.50',
                  position: 'relative',
                  overflow: 'hidden'
              }}
          >
              <Container maxWidth="lg">
                  <Fade in timeout={1000}>
                      <Paper
                          elevation={0}
                          sx={{
                              p: { xs: 6, md: 10 },
                              textAlign: 'center',
                              borderRadius: 6,
                              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                              color: '#FFFFFF',
                              position: 'relative',
                              overflow: 'hidden',
                              '&::before': {
                                  content: '""',
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  bottom: 0,
                                  background: 'url(data:image/svg+xml,<svg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 100 100\'><circle cx=\'50\' cy=\'50\' r=\'2\' fill=\'#FFFFFF\' opacity=\'0.1\'/></svg>) repeat',
                                  backgroundSize: '50px 50px'
                              }
                          }}
                      >
                          <Box sx={{ position: 'relative', zIndex: 1 }}>
                              <AutoAwesome sx={{ fontSize: 80, mb: 3, opacity: 0.9 }} />

                              <Typography
                                  variant="h2"
                                  sx={{
                                      fontWeight: 800,
                                      mb: 3,
                                      fontSize: { xs: '2.5rem', md: '3.5rem' }
                                  }}
                              >
                                  Ready to Join Our Community?
                              </Typography>

                              <Typography
                                  variant="h5"
                                  sx={{
                                      mb: 6,
                                      opacity: 0.95,
                                      maxWidth: 800,
                                      mx: 'auto',
                                      lineHeight: 1.6,
                                      fontSize: { xs: '1.3rem', md: '1.5rem' }
                                  }}
                              >
                                  Take the first step towards your child's bright future. Schedule time with us to explore our curriculum,
                                  and discover why Eagles Refuge Academy is the right choice.
                              </Typography>

                              {/* Schedule Call Button */}
                              <Box sx={{ maxWidth: 800, mx: 'auto', mb: 6, mt: 6 }}>
                                  <ScheduleCallModal />
                              </Box>

                              {/* Contact Info */}

                              {/*<Grid container spacing={4} sx={{ maxWidth: 800, mx: 'auto' }}>*/}
                              {/*    <Grid item xs={12} md={4}>*/}
                              {/*        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>*/}
                              {/*            <Phone sx={{ mr: 2, opacity: 0.9 }} />*/}
                              {/*            <Box>*/}
                              {/*                <Typography variant="body2" sx={{ opacity: 0.8 }}>*/}
                              {/*                    Call Us*/}
                              {/*                </Typography>*/}
                              {/*                <Typography variant="h6" sx={{ fontWeight: 600 }}>*/}
                              {/*                    (555) 123-4567*/}
                              {/*                </Typography>*/}
                              {/*            </Box>*/}
                              {/*        </Box>*/}
                              {/*    </Grid>*/}
                              {/*    <Grid item xs={12} md={4}>*/}
                              {/*        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>*/}
                              {/*            <Email sx={{ mr: 2, opacity: 0.9 }} />*/}
                              {/*            <Box>*/}
                              {/*                <Typography variant="body2" sx={{ opacity: 0.8 }}>*/}
                              {/*                    Email Us*/}
                              {/*                </Typography>*/}
                              {/*                <Typography variant="h6" sx={{ fontWeight: 600 }}>*/}
                              {/*                    info@eaglesrefuge.edu*/}
                              {/*                </Typography>*/}
                              {/*            </Box>*/}
                              {/*        </Box>*/}
                              {/*    </Grid>*/}
                              {/*    <Grid item xs={12} md={4}>*/}
                              {/*        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>*/}
                              {/*            <LocationOn sx={{ mr: 2, opacity: 0.9 }} />*/}
                              {/*            <Box>*/}
                              {/*                <Typography variant="body2" sx={{ opacity: 0.8 }}>*/}
                              {/*                    Visit Us*/}
                              {/*                </Typography>*/}
                              {/*                <Typography variant="h6" sx={{ fontWeight: 600 }}>*/}
                              {/*                    123 Education Blvd*/}
                              {/*                </Typography>*/}
                              {/*            </Box>*/}
                              {/*        </Box>*/}
                              {/*    </Grid>*/}
                              {/*</Grid>*/}
                          </Box>
                      </Paper>
                  </Fade>
              </Container>
          </Box>
      </Box>
    );
};

export default Home;
